import { FormikProps, useFormik } from 'formik'
import React, { ReactNode, useState } from 'react'
import ModalComponent from '..'
import { Button } from '../../button'
import { TextInput } from '../../inputs/textInput'

import { ExportReportSchema } from '../../../validation/validate'

import { format } from 'date-fns'
import { ObjectSchema } from 'yup'
import { parseDate } from '../../../helpers'
import Icon from '../../icon'
import { DateRangePicker } from '../../inputs/dateRange'
import Dropdown from '../../inputs/dropdown'
import { TabNav } from '../../nav/tabNav'
import { useExportStakeholdersReportMutation } from '../../../redux-toolkits/reports/reports.slice'

export const ExportDataModal: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: () => void
  extraValues?: {
    [key: string]: string | boolean | undefined
  }
  title: string
  children?: (form: FormikProps<any>) => ReactNode
  schema?: ObjectSchema<any>
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = () => {},
  extraValues = {},
  title,
  children,
  schema,
}) => {
  const [actionType, setActionType] = useState<'export' | 'download'>('export')

  const [exportReport, { isLoading: exporting }] =
    useExportStakeholdersReportMutation()

  const generalForm = useFormik({
    initialValues: {
      startDate: '',
      endDate: '',
      isCustomDateRange: false,
      filter: '',
      type: '',
      ...extraValues,
    },
    validationSchema: schema
      ? ExportReportSchema.concat(schema)
      : ExportReportSchema,
    onSubmit: async (values) => {
      const params = Object.entries(values).reduce(
        (acc: Record<string, string | boolean>, [key, value]) => {
          if (value !== '') acc[key] = value
          return acc
        },
        {} as Record<string, string>,
      )

      if (actionType === 'export') {
        const resp = await exportReport({ type: values.type })

        if (resp.data?.success) {
          onSuccessCallback()
          generalForm.resetForm()
          setOpen(false)
          onCloseCallback()
        }
      }
    },
  })

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        generalForm.resetForm()
        onCloseCallback()
      }}
      title={`${title}`}
      childrenClassName="md:!max-w-[420px]"
    >
      <form className="flex flex-col gap-4" onSubmit={generalForm.handleSubmit}>
        <TabNav
          activeTab={generalForm.values.isCustomDateRange ? 'date' : 'period'}
          setActiveTab={(tab) => {
            generalForm.setFieldValue(
              'isCustomDateRange',
              tab === 'period' ? false : true,
            )

            if (tab === 'period') {
              generalForm.setFieldValue('startDate', '')
              generalForm.setFieldValue('endDate', '')
            } else {
              generalForm.setFieldValue('filter', '')
            }
          }}
          tabs={[
            { name: 'Period', id: 'period' },
            { name: 'Date Range', id: 'date' },
          ]}
          type="switcher"
          size="regular"
          className="w-full"
        />

        {generalForm.values.isCustomDateRange ? (
          <Dropdown
            menuButton={
              <TextInput
                value={`${
                  generalForm.values.startDate
                    ? format(generalForm.values.startDate!, 'dd MMM, yyyy')
                    : 'Start date'
                } - ${
                  generalForm.values.endDate
                    ? format(generalForm.values.endDate!, 'dd MMM, yyyy')
                    : 'End date'
                }`}
                title="Date range"
                hasIcon={true}
                iconSvg="filter"
                onBlur={() => {}}
                onChange={() => {}}
              />
            }
            menuClassName="!min-w-max !border-[0px]"
            customMenuItems={
              <DateRangePicker
                required
                animate
                navLayout="around"
                mode="range"
                selected={{
                  from:
                    parseDate(generalForm.values.startDate)?.toDate() ??
                    undefined,
                  to:
                    parseDate(generalForm.values.endDate)?.toDate() ??
                    undefined,
                }}
                onSelect={(selected) => {
                  if (selected?.from && selected?.to) {
                    generalForm.setFieldValue(
                      'startDate',
                      format(selected?.from, 'yyyy-MM-dd'),
                    )
                    generalForm.setFieldValue(
                      'endDate',
                      format(selected?.to, 'yyyy-MM-dd'),
                    )
                  }
                }}
                numberOfMonths={2}
                disabled={{ after: new Date() }}
              />
            }
          />
        ) : (
          <Dropdown
            menuClassName="!max-h-[200px]"
            menuButton={
              <TextInput
                type="text"
                name="filter"
                placeholder="Period"
                title="Period"
                value={generalForm?.values?.filter}
                onBlur={generalForm.handleBlur}
                onChange={generalForm.handleChange}
                errors={
                  generalForm.touched.filter && generalForm.errors.filter
                    ? generalForm.errors.filter
                    : ''
                }
                hasIcon={false}
                extra={
                  <Icon
                    id={'left-caret'}
                    width={16}
                    height={16}
                    className="-rotate-90 text-sec-black"
                  />
                }
              />
            }
            menuItems={[
              { name: 'Today', value: 'today' },
              { name: 'Yesterday', value: 'yesterday' },
              { name: 'This week', value: 'this_week' },
              { name: 'This month', value: 'this_month' },
              { name: 'This year', value: 'this_year' },
              { name: 'Last month', value: 'last_month' },
              { name: 'Last week', value: 'last_week' },
              { name: 'Last year', value: 'last_year' },
              { name: 'ITD', value: 'inception_to_date' },
            ].map((itm) => ({
              ...itm,
              onClick: () => {
                generalForm.setFieldValue(
                  'filter',
                  itm.value === 'all' ? undefined : (itm.value as string),
                )
              },
            }))}
          />
        )}

        <Dropdown
          menuClassName="!max-h-[200px]"
          menuButton={
            <TextInput
              type="text"
              name="type"
              placeholder="Export Type"
              title="Export Type"
              value={generalForm?.values?.type}
              onBlur={generalForm.handleBlur}
              onChange={generalForm.handleChange}
              errors={
                generalForm.touched.type && generalForm.errors.type
                  ? generalForm.errors.type
                  : ''
              }
              hasIcon={false}
              extra={
                <Icon
                  id={'left-caret'}
                  width={16}
                  height={16}
                  className="-rotate-90 text-sec-black"
                />
              }
            />
          }
          menuItems={[
            { name: 'Settlements', value: 'settlements' },
            { name: 'Revenue', value: 'revenue' },
          ].map((itm) => ({
            ...itm,
            onClick: () => {
              generalForm.setFieldValue(
                'type',
                itm.value === 'all' ? undefined : (itm.value as string),
              )
            },
          }))}
        />

        {children ? children(generalForm) : undefined}

        <div className="flex flex-col mt-10 ">
          <Button
            label="Export"
            type="submit"
            onClick={() => {
              setActionType('export')
            }}
            className={
              generalForm?.values?.type ? '' : 'bg-slate-200 border-slate-200'
            }
            disabled={generalForm?.values?.type ? exporting : true}
            loading={exporting}
            primary
          />
        </div>
      </form>
    </ModalComponent>
  )
}
